
<script setup>
    import { computed } from 'vue';
    import HeatmapChart from '@/components/result/ai-literacy/HeatmapChart.vue';

    const props = defineProps({
        teacherData: Object
    });

    // 초등학교(E) 문제 매핑 정보
    const elementarySchoolMapping = {
        'a': [ // AI·디지털 기술 (13개)
            { q_no: 1, subject: '얼굴인식 과정' },
            { q_no: 4, subject: '지능형 로봇의 사례' },
            { q_no: 5, subject: '추천시스템에 대한 설명' },
            { q_no: 11, subject: '인공지능 사용 예시' },
            { q_no: 12, subject: '데이터 전처리' },
            { q_no: 13, subject: '데이터 라벨링' },
            { q_no: 14, subject: '데이터 학습' },
            { q_no: 17, subject: '인공지능에 적합한 작업' },
            { q_no: 18, subject: '스팸 분류' },
            { q_no: 19, subject: '인공지능 모델 제작' },
            { q_no: 22, subject: '생성형 AI의 특징' },
            { q_no: 24, subject: 'AI 활용에 대한 비판적 태도' },
            { q_no: 25, subject: 'AI 활용 그림 개선' }
        ],
        'b': [ // AI·디지털 활용 (6개)
            { q_no: 2, subject: '실생활 문제 해결' },
            { q_no: 6, subject: '추천시스템의 절차' },
            { q_no: 7, subject: '인공지능의 학습 활용' },
            { q_no: 8, subject: '추천시스템의 원리' },
            { q_no: 9, subject: '추천시스템의 원리' },
            { q_no: 27, subject: 'AI의 다양한 활용 분야' }
        ],
        'c': [ // AI·디지털 윤리 (5개)
            { q_no: 3, subject: '개인정보보호의 중요성' },
            { q_no: 10, subject: '올바른 추천시스템 활용법' },
            { q_no: 16, subject: '인공지능의 사회적 영향' },
            { q_no: 21, subject: '스팸메일 특징과 주의점' },
            { q_no: 26, subject: '기후위기 문구 만들기' }
        ],
        'd': [ // 인간 중심 AI (3개)
            { q_no: 15, subject: '인공지능과 협업' },
            { q_no: 20, subject: '블록코딩' },
            { q_no: 23, subject: '인공지능과 지속가능발전' }
        ]
    };

    // 중학교(M) 문제 매핑 정보
    const middleSchoolMapping = {
        'a': [ // AI·디지털 기술 (12개)
            { q_no: 1, subject: '탐색을 위한 데이터 수집' },
            { q_no: 5, subject: 'AI의 특징과 주요 원리' },
            { q_no: 6, subject: '추천을 위한 데이터 수집' },
            { q_no: 10, subject: 'AI 비서의 특징' },
            { q_no: 12, subject: 'AI 비서의 원리' },
            { q_no: 16, subject: 'AI 영상 추천의 주요 원리 및 방법' },
            { q_no: 17, subject: '추천 데이터의 원리와 데이터' },
            { q_no: 18, subject: '데이터에 따른 영상 추천' },
            { q_no: 19, subject: '추천 시스템의 데이터와 학습' },
            { q_no: 23, subject: '생성형 AI의 개념' },
            { q_no: 24, subject: '생성형 AI의 사용 과정' },
            { q_no: 25, subject: '생성형 AI의 원리' }
        ],
        'b': [ // AI·디지털 활용 (5개)
            { q_no: 7, subject: '학습에서의 AI 활용' },
            { q_no: 8, subject: 'AI를 활용한 문제 해결' },
            { q_no: 20, subject: '추천 시스템을 활용한 문제 해결' },
            { q_no: 26, subject: 'AI 활용 그림 창작' },
            { q_no: 27, subject: 'AI 활용 그림 개선' }
        ],
        'c': [ // AI·디지털 윤리 (8개)
            { q_no: 3, subject: 'AI로 변화하는 일상생활' },
            { q_no: 4, subject: 'AI 윤리 원칙' },
            { q_no: 9, subject: 'AI 결과의 활용 자세' },
            { q_no: 13, subject: 'AI로 변화되는 사회' },
            { q_no: 14, subject: 'AI의 윤리적 딜레마' },
            { q_no: 15, subject: 'AI 결과의 편향 제거' },
            { q_no: 21, subject: '추천 시스템의 비판적 검토' },
            { q_no: 28, subject: 'AI 결과의 비판적 검토' }
        ],
        'd': [ // 인간 중심 AI (3개)
            { q_no: 2, subject: '자율주행 프로토타입 만들기' },
            { q_no: 11, subject: '인간과 AI의 공통점과 차이점' },
            { q_no: 22, subject: '인간과 AI의 그림 비교' }
        ]
    };

    // teacherData를 기반으로 실제 차트 데이터 생성
    const sampleData = computed(() => {
        if (!props.teacherData) {
            return [
                {
                    category: 'AI·디지털 기술',
                    rate: 78.5
                },
                {
                    category: 'AI·디지털 활용',
                    rate: 84.2
                },
                {
                    category: 'AI·디지털 윤리',
                    rate: 76.8
                },
                {
                    category: '인간 중심 AI',
                    rate: 82.3
                }
            ];
        }

        const categories = {
            'field1': 'AI·디지털 기술',
            'field2': 'AI·디지털 활용',  
            'field3': 'AI·디지털 윤리',
            'field4': '인간 중심 AI'
        };

        return [
            {
                category: categories['field1'],
                rate: parseFloat(props.teacherData.field1_rate) || 0
            },
            {
                category: categories['field2'],
                rate: parseFloat(props.teacherData.field2_rate) || 0
            },
            {
                category: categories['field3'],
                rate: parseFloat(props.teacherData.field3_rate) || 0
            },
            {
                category: categories['field4'],
                rate: parseFloat(props.teacherData.field4_rate) || 0
            }
        ];
    });

    import icon01 from '@/assets/images/result/ai/icon_01.svg';
    import icon02 from '@/assets/images/result/ai/icon_02.svg';
    import icon03 from '@/assets/images/result/ai/icon_03.svg';
    import icon04 from '@/assets/images/result/ai/icon_04.svg';
    import qrImage from '@/assets/images/result/ai_qr.png';
    import qrFrame from '@/assets/images/result/ai/qr_frame.svg';

    // 영역별 정보
    const fieldInfo = {
        'a': { name: 'AI·디지털 기술', icon: icon01 },
        'b': { name: 'AI·디지털 활용', icon: icon02 },
        'c': { name: 'AI·디지털 윤리', icon: icon03 },
        'd': { name: '인간 중심 AI', icon: icon04 }
    };

    // 설명 데이터 (학교급별 분리)
    const commentData = {
        'E': { // 초등학교
            'a': {
                's': '일상생활 속 다양한 인공지능 사례에 높은 호기심을 보이며, 인공지능과 비인공지능을 명확히 구별할 수 있습니다. 특히 기계학습의 기초적인 개념과 데이터의 중요성을 이해하고, 텍스트나 이미지 등 데이터 유형에 따른 수집 및 분석 과정을 원활하게 수행합니다.',
                'w': '기계학습 및 딥러닝의 작동 원리나 생성형 AI의 결과물이 도출되는 주요 원리 등 기술적 하부 구조에 대한 이해도가 낮습니다. 이는 단순 체험 위주의 학습에 치중될 때 나타나는 특성으로, 알고리즘의 원리에 대한 단계별 탐구 학습이 보완되어야 합니다.'
            },
            'b': {
                's': 'AI 디지털 도구를 활용하여 문자, 그림, 소리 등 다양한 형태의 콘텐츠를 창작하는 체험에 능숙하며, 이를 통해 자신의 아이디어를 새롭게 표현하려는 태도를 보입니다. 실생활의 문제를 해결하거나 학습 상황에서 적절한 도구를 선택해 활용하는 응용 능력이 우수합니다.',
                'w': '다양한 AI 디지털 도구의 장단점을 비교하여 객관적으로 평가하거나, 도구가 창작에 활용될 때의 가치를 분석하는 비판적 사고 능력이 부족합니다. 도구를 기능적으로만 사용하는 수준에 머무를 수 있어, 도구 활용의 결과에 대한 자기 성찰적 평가 교육이 필요합니다.'
            },
            'c': {
                's': '개인정보 보호의 중요성을 명확히 인지하고 있으며, 인공지능 사용 시 저작권 존중 및 타인의 안전 보호와 같은 책임감 있는 태도를 실천합니다. 인공지능 기술이 사회에 주는 이로움과 해로움을 파악하여 주체적으로 기술을 수용하려는 자세를 갖추고 있습니다.',
                'w': '인공지능 과의존 문제나 딥페이크와 같은 구체적인 윤리적 이슈에 대해 실제적인 대응 및 예방 수칙을 실천하는 데 어려움을 보입니다. 지식적으로는 윤리 규범을 인지하고 있으나 실제 디지털 환경에서의 실천력이 낮으므로, 사례 중심의 윤리적 문제 해결 훈련이 강화되어야 합니다.'
            },
            'd': {
                's': '인공지능을 단순한 도구가 아닌 새로운 협업 대상으로 인식하며, 인간과 인공지능의 차이(인식 및 학습 방식 등)를 비교 분석하려는 탐구심이 강합니다. 지속 가능한 사회 발전을 위해 인공지능이 기여할 수 있는 사례에 관심을 갖고 주체적으로 참여하려는 의지를 보입니다.',
                'w': '우리 주변에서 인공지능이 잘 수행할 수 있는 구체적인 문제를 정의하거나, 인간의 가치에 부합하는 인공지능 활용 아이디어를 글이나 도식으로 구체화하는 설계 능력이 다소 미흡합니다. 이는 인권, 사회 정의 등 추상적 가치와 기술을 연결 짓는 경험이 부족하기 때문으로, 인간 중심 설계 관점의 프로젝트 활동이 권장됩니다.'
            }
        },
        'M': { // 중학교
            'a': {
                's': '인공지능이 수행하는 예측, 분석, 추천, 생성 등의 주요 특징을 사례 중심으로 명확히 구별할 수 있습니다. 특히 지도학습, 비지도학습, 강화학습 등 기계학습의 유형별 차이를 이해하고 있으며, 수집된 데이터를 직접 구조화하고 시각화하여 간단한 기계학습 모델을 스스로 제작하는 실천적 역량이 우수합니다.',
                'w': '생성형 AI의 기본 원리와 다른 AI 기술과의 차이점을 지식적으로 설명하는 데 어려움을 보입니다. 또한, 학습 데이터의 편향성이 인공지능 모델에 미치는 구체적인 영향력을 간과하는 경향이 있어, 데이터 수집 및 분석 과정에서의 체계적인 원리 교육이 필요합니다.'
            },
            'b': {
                's': '실생활 문제를 해결하기 위해 적합한 AI·디지털 도구를 능동적으로 선택하여 활용할 수 있습니다. 텍스트, 이미지, 영상 등 다양한 도구의 기능을 이해하여 창의적인 결과물을 제작하며, 인공지능이 생성한 콘텐츠임을 명확히 표시하고 저작권을 준수하는 책임감 있는 활용 습관을 갖추고 있습니다.',
                'w': '여러 산업 분야에서 AI 디지털 기술이 융합되어 문제를 해결한 구체적인 사례를 탐색하고 분석하는 능력이 다소 미흡합니다. 또한 활용 과정에서 도구가 도출한 결과물의 신뢰성과 적절성을 객관적으로 평가하기보다는 결과물을 그대로 수용하는 모습을 보이기도 합니다.'
            },
            'c': {
                's': '딥페이크나 가짜 뉴스 등 인공지능 오남용이 개인과 사회에 미치는 위험성을 정확히 식별하고, 개인정보 침해를 예방하는 수칙을 실천합니다. 인공지능의 편향성 및 공정성 이슈를 이해하고, 결과물에 나타난 편향을 비판적으로 검토하여 제거하려는 주체적인 자세를 보입니다.',
                'w': '데이터 소유권과 개인정보 보호 사이의 갈등 등 인공지능과 관련된 고도화된 윤리적 딜레마 상황에서 주된 원인을 찾아내고 해결 방안을 모색하는 데 어려움을 느낍니다. 이는 단순 규범 암기를 넘어 사회적 영향력을 고려한 심화 토론과 사례 분석이 보완되어야 함을 시사합니다.'
            },
            'd': {
                's': '인공지능이 인간의 지능과 구별되는 공통점과 차이점을 비교 분석할 수 있으며, 인공지능을 새로운 협업 대상으로 인식합니다. 특히 지속 가능한 사회 발전을 위해 인공지능이 기여할 수 있는 역할을 정의하고, 이를 해결하기 위한 아이디어를 프로토타입 형태로 구상하는 설계 역량이 돋보입니다.',
                'w': '인공지능이 잘하는 일과 인간이 잘하는 일을 다양한 영역에서 객관적으로 비교 분석하여 자신의 역할을 정립하는 데 한계를 보입니다. 인간 중심의 인공지능 시스템이 사회 정의나 포용성 측면에서 올바르게 설계되었는지 비판적으로 검토하는 시각을 기르는 교육이 뒷받침되어야 합니다.'
            }
        }
    };

    // 강점 영역 정보
    const strengthInfo = computed(() => {
        if (!props.teacherData) {
            return {
                field: 'd',
                name: '인간 중심 AI',
                icon: icon04,
                description: commentData['E']['d']['s']
            };
        }
        
        const schulType = props.teacherData.schul_type || 'E';
        let field = props.teacherData.strong_field || 'd';
        // 유효하지 않은 field 값인 경우 기본값 사용
        if (!fieldInfo[field]) {
            field = 'd';
        }
        
        return {
            field: field,
            name: fieldInfo[field].name,
            icon: fieldInfo[field].icon,
            description: commentData[schulType][field]['s']
        };
    });

    // 약점 영역 정보
    const weaknessInfo = computed(() => {
        if (!props.teacherData) {
            return {
                field: 'b',
                name: 'AI·디지털 활용',
                icon: icon02,
                description: commentData['E']['b']['w']
            };
        }
        
        const schulType = props.teacherData.schul_type || 'E';
        let field = props.teacherData.weak_field || 'b';
        // 유효하지 않은 field 값인 경우 기본값 사용
        if (!fieldInfo[field]) {
            field = 'b';
        }
        
        return {
            field: field,
            name: fieldInfo[field].name,
            icon: fieldInfo[field].icon,
            description: commentData[schulType][field]['w']
        };
    });

    // 강점 문항 정보 생성
    const strengthQuestions = computed(() => {
        if (!props.teacherData) {
            return [
                { qNo: 3, subject: '인간과 AI의 그림 비교', rate: '68.18' },
                { qNo: 2, subject: '인간과 AI의 공통점과 차이점', rate: '63.64' },
                { qNo: 1, subject: '자율주행 프로토타입 만들기', rate: '54.55' }
            ];
        }

        const currentMapping = props.teacherData.schul_type === 'E' 
            ? elementarySchoolMapping 
            : middleSchoolMapping;

        return [1, 2, 3].map(i => {
            const materialKey = `strong${i}_materials`;
            const qNo = props.teacherData[materialKey];
            const rateKey = `q_${qNo}_correct_rate`;
            const rate = parseFloat(props.teacherData[rateKey]) || 0;
            
            // 해당 문항의 subject와 영역 내 순서 찾기
            let subject = '문항';
            let questionNumber = qNo; // 기본값은 원래 번호
            
            for (const code of Object.keys(currentMapping)) {
                const found = currentMapping[code].findIndex(item => item.q_no === qNo);
                if (found !== -1) {
                    subject = currentMapping[code][found].subject;
                    questionNumber = found + 1; // 영역 내 순서 (1부터 시작)
                    break;
                }
            }
            
            return {
                qNo: questionNumber,
                subject: subject,
                rate: rate.toFixed(1)
            };
        }).filter((item, index) => {
            const materialKey = `strong${index + 1}_materials`;
            return props.teacherData[materialKey]; // 해당 인덱스에 데이터가 있는지 확인
        });
    });

    // 약점 문항 정보 생성
    const weaknessQuestions = computed(() => {
        if (!props.teacherData) {
            return [
                { qNo: 1, subject: '문항', rate: '68.18' },
                { qNo: 2, subject: '문항', rate: '63.64' },
                { qNo: 3, subject: '문항', rate: '54.55' }
            ];
        }

        const currentMapping = props.teacherData.schul_type === 'E' 
            ? elementarySchoolMapping 
            : middleSchoolMapping;

        return [1, 2, 3].map(i => {
            const materialKey = `weak${i}_materials`;
            const qNo = props.teacherData[materialKey];
            const rateKey = `q_${qNo}_correct_rate`;
            const rate = parseFloat(props.teacherData[rateKey]) || 0;
            
            // 해당 문항의 subject와 영역 내 순서 찾기
            let subject = '문항';
            let questionNumber = qNo; // 기본값은 원래 번호
            
            for (const code of Object.keys(currentMapping)) {
                const found = currentMapping[code].findIndex(item => item.q_no === qNo);
                if (found !== -1) {
                    subject = currentMapping[code][found].subject;
                    questionNumber = found + 1; // 영역 내 순서 (1부터 시작)
                    break;
                }
            }
            
            return {
                qNo: questionNumber,
                subject: subject,
                rate: rate.toFixed(1)
            };
        }).filter((item, index) => {
            const materialKey = `weak${index + 1}_materials`;
            return props.teacherData[materialKey]; // 해당 인덱스에 데이터가 있는지 확인
        });
    });
</script>
<template>
  <div class="report-inner">   
    <div class="card-wrap">
        <HeatmapChart :data="sampleData" />        
        <div class="card mt-4">
            <div class="ai-table primary">
                <div class="row thead">
                    <div class="th col">
                        <div class="th">구분</div>
                        <div class="th">영역</div>
                    </div>                    
                    <div class="td">설명</div>
                </div>
                <div class="row">
                    <div class="th col">
                        <div class="th">강점</div>
                        <div class="th">
                             <img :src="strengthInfo.icon">
                            <p>{{ strengthInfo.name }}</p>
                        </div>
                    </div>
                    <div class="td">
                        {{ strengthInfo.description }}
                        <ul>
                            <li v-for="question in strengthQuestions" :key="question.qNo">
                                {{ question.subject }}: {{ question.rate }}%
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="row">
                    <div class="th col">
                        <div class="th">약점</div>
                        <div class="th">
                             <img :src="weaknessInfo.icon">
                            <p>{{ weaknessInfo.name }}</p>
                        </div>
                    </div>
                    <div class="td">
                        {{ weaknessInfo.description }}
                        <ul>
                            <li v-for="question in weaknessQuestions" :key="question.qNo">
                                {{ question.subject }}: {{ question.rate }}%
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="row">
                    <div class="th col">
                        <div class="th">AI·디지털<br>리터러시<br>수업 도움자료<br>(공통자료)</div>
                        <div class="th">                            
                            <p>AI·디지털 기술</p>
                            <p>AI·디지털 활용</p>
                            <p>AI·디지털 윤리</p>
                            <p>인간 중심 AI</p>
                        </div>
                    </div>
                    <div class="td">
                        <div class="td-content">
                            <ul>
                                <li>서울교육포털 – AI·SW교구 활용 수업자료 가이드북<br/><a href="https://tinyurl.com/2adnyjlc">https://tinyurl.com/2adnyjlc</a></li>
                                <li>KERIS – 생성형 AI를 활용한 교수학습 운영 가이드<br/><a href="https://tinyurl.com/2d8qn93l">https://tinyurl.com/2d8qn93l</a></li>
                                <li>
                                    <p>인공지능 윤리 소통채널 – 초·중·고 AI 윤리 교재<br/><a href="https://ai.kisdi.re.kr/aieth/bbs/B0000082/list.do?menuNo=400011">https://ai.kisdi.re.kr/aieth/bbs/B0000082/list.do?menuNo=400011</a></p>
                                    <p class="mt-1">디지털윤리.kr – 짧게 끝내는 생성형 AI윤리 수업<br/><a href="https://tinyurl.com/2a8sth9u">https://tinyurl.com/2a8sth9u</a></p>
                                </li>
                                <li>AI4TEACHER – 사람을 돕는 착한 AI<br/><a href="https://www.ai4teacher.com/good-ai">https://www.ai4teacher.com/good-ai</a></li>                                
                            </ul>                           
                        </div>
                        <div class="qr-area">
                            <p>서울특별시교육청<br>디지털 기반<br>교육생태계 도움자료</p>
                            <div class="qr-placeholder" :style="{ backgroundImage: `url(${qrFrame})` }">
                                <div class="qr-img"><img :src="qrImage" alt="QR코드"></div>
                            </div>
                            <a href="https://fliphtml5.com/bookcase/bucrw/">https://fliphtml5.com/<br/>bookcase/bucrw/</a>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ai-table {
    .row div.th.col {
        padding: 0;
       display: flex;  
        &::before { display: none; }
        .th { 
            position: relative;
            padding: 1rem 0 1rem 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 200px;
            height: 100%;
            min-width: auto;
            &::before {
                content: "";
                position: absolute;
                background-color: var(--result-ai-primary-100);
                top: 50%;
                right: 0;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 10px);
            }
            &:nth-child(1) { font-weight: 600; text-align: center; width: 150px; }
            &:nth-child(2) { 
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 180px;
                p { font-weight: 600; color: var(--result-ai-primary-800); }
            }
        }  
    }
    .td {
        
        line-height: 1.5;
        text-align: justify;
        word-break: keep-all;
        text-justify: inter-word;
         > ul {
            li { 
            position: relative;
            padding-left: 24px;
            &::before {
                content: '';
                position: absolute;
                left: 10px;
                top: 11px;
                width: 3px;
                height: 3px;
                background-color: var(--result-ai-blak);
                border-radius: 50%;       
                }
            }
        }
        .qr-area {
            flex: 3;
            padding: 1rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            p { text-align: center; font-weight: 600; color: var(--result-ai-primary-800); }
            a { 
                font-size: 1rem; 
                color: var(--result-ai-primary-500);
                display: inline-block;
                text-decoration: underline;
                text-underline-offset: 3px;
                margin-top: 0.5rem;
                word-break: break-all;
                line-height: 1.3;
                width: 100%;
                text-align: center;
            }
            .qr-placeholder {
                position: relative;
                width: 162px;
                height: 232px;
                .qr-img {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    img {
                        margin-top: 40px;
                        width: 109px;
                        height: 111px;
                    }
                }
            }
            &::before {
                content: "";
                position: absolute;
                background-color: var(--result-ai-primary-100);
                top: 50%;
                left: 0;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 10px);
            }
        }
    }
    .row:last-child {
        .th {
            &:nth-child(2) { 
                padding: 0 !important; 
                &::before { display: none; }            
            }           
            p { 
                position: relative;
                width: 100%;
                height: 21%;
                display: flex;
                align-items: center;
                justify-content: center;               
                &::before {
                    content: "";
                    position: absolute;
                    background-color: var(--result-ai-primary-100);
                    top: 50%;
                    right: 0;
                    transform: translateY(-50%);
                    width: 1px;
                    height: calc(100% - 10px);
                }
                &::after {
                    content: "";
                    position: absolute;
                    background-color: var(--result-ai-primary-100);
                    right : 0;
                    bottom: 0;
                    width: calc(100% - 10px);
                    height: 1px;
                }
                &:nth-child(3) { height: 37%; }
                &:last-child::after { display: none; }
            }
        }
        .td { 
            display: flex; padding: 0 !important;  
            height: 400px;           
            .td-content {
                flex: 6;
                ul {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    text-align: left;
                    li { 
                        height: 21%;
                        display: flex;
                        align-items: flex-start;
                        justify-content: center;
                        flex-direction: column;
                        padding: .5rem 1rem;
                        position: relative;
                        line-height: 1.3;
                        &:nth-child(3) { height: 37%; }
                        &::after {
                            content: "";
                            position: absolute;
                            background-color: var(--result-ai-primary-100);
                            left : 0;
                            bottom: 0;
                            width: calc(100% - 10px);
                            height: 1px;
                        }
                        &:last-child::after { display: none; }
                        a { 
                            font-size: 1rem; 
                            color: var(--result-ai-primary-500);
                            display: inline-block;
                            text-decoration: underline;
                            text-underline-offset: 3px;
                         }
                    }
                }
            }
        }
    }
    .row.thead div.th.col .th {
        &::before {
            width: 2px !important;
            height: 100% !important;
        }
    }    
}
</style>